import type { HelpView } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { Compass, FileDown, LifeBuoy, Lightbulb, ListChecks } from 'lucide-react';
import { api } from '../lib/api';
import { useMe } from '../lib/session';
import { supportUi, useSupportUi } from '../lib/support';
import { PathArticles } from '../pages/kb/RelatedArticles';
import { HelpPilot } from '../pages/support/HelpPilot';
import { Button } from '../ui/button';
import { Sheet } from '../ui/dialog';
import { ErrorBox, Spinner } from '../ui/feedback';

/**
 * Кнопка «Помощь» на каждой странице (БЗ-01): инструкция к этой странице для роли человека, частые вопросы, переход
 * в поддержку одним нажатием, инструкция по роли в PDF и экскурсия. Инструкцию собирает сервер из прав человека и
 * описаний разделов (БЗ-02): здесь только то, что открыто ему. Поле вопроса Pilot — когда модель подключена и у
 * человека есть право на ИИ-помощника (сервер сообщает полем `pilot.available`); иначе вопрос уходит в поддержку.
 */
export function HelpPanel() {
  const { help } = useSupportUi();
  return (
    <Sheet
      open={help}
      onOpenChange={(v) => (v ? supportUi.openHelp() : supportUi.closeHelp())}
      title="Помощь"
      description="Инструкция к этой странице для вашей роли"
    >
      {help ? <HelpBody /> : null}
    </Sheet>
  );
}

function HelpBody() {
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const q = useQuery({
    queryKey: ['support', 'help', path],
    queryFn: () => api<HelpView>('/support/help', { query: { path } }),
    staleTime: 5 * 60_000,
  });
  const staff = me.tenant?.staff ?? false;
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const h = q.data;
  return (
    <div className="flex flex-col gap-5 text-[14px] text-ink">
      <div>
        <h3 className="text-[16px] leading-6 font-semibold">{h.title}</h3>
        <p className="mt-1 text-ink-2">{h.summary}</p>
        <p className="mt-2 text-[12px] text-ink-2">Ваша роль: {h.roles}</p>
      </div>

      {h.sections.map((s) => (
        <section key={s.title}>
          <h4 className="mb-1.5 text-[13px] font-semibold text-ink">{s.title}</h4>
          <ul className="flex list-disc flex-col gap-1 pl-5 leading-6">
            {s.items.map((it) => (
              <li key={it}>{it}</li>
            ))}
          </ul>
        </section>
      ))}

      <PathArticles path={path} onOpen={() => supportUi.closeHelp()} />

      {h.faq.length ? (
        <section>
          <h4 className="mb-1.5 text-[13px] font-semibold text-ink">Частые вопросы</h4>
          <div className="flex flex-col gap-2">
            {h.faq.map((f) => (
              <details key={f.q} className="rounded-md border border-line bg-surface-2 px-3 py-2">
                <summary className="min-h-11 cursor-pointer py-2.5 text-[14px] font-medium md:min-h-0 md:py-0">
                  {f.q}
                </summary>
                <p className="pt-1 pb-1 leading-6 text-ink-2">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      ) : null}

      {h.pilot.available ? <HelpPilot path={path} staff={staff} /> : null}

      <section className="flex flex-col gap-2 rounded-lg border border-line bg-surface-2 p-4">
        <h4 className="text-[14px] font-semibold">Не нашли ответ?</h4>
        {staff ? (
          <>
            <p className="text-ink-2">
              Напишите в поддержку: обращение уйдёт с этой страницей, снимком экрана и вашей ролью, ответит
              человек. Если из-за ошибки стоит работа, ответим в течение 2 часов, в остальных случаях — в
              течение 4 рабочих часов. То же — кнопкой «Сообщить» вверху любой страницы.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                icon={<LifeBuoy className="size-4" />}
                onClick={() => supportUi.openReport('question')}
              >
                Написать в поддержку
              </Button>
              <Button icon={<Lightbulb className="size-4" />} onClick={() => supportUi.openReport('idea')}>
                Предложить улучшение
              </Button>
            </div>
          </>
        ) : (
          <p className="text-ink-2">
            Внешним участникам обращения в поддержку не доступны. Напишите контактному лицу в компании,
            которая вас пригласила.
          </p>
        )}
        {h.pilot.available ? null : (
          <p className="text-[12px] text-ink-2">
            Pilot — помощник портала — подключится позже. Пока отвечает поддержка.
          </p>
        )}
      </section>

      <section className="flex flex-col gap-1">
        <Button
          variant="ghost"
          className="justify-start"
          icon={<Compass className="size-4" />}
          onClick={() => supportUi.openTour()}
        >
          Экскурсия по разделам
        </Button>
        <Link
          to="/start"
          onClick={() => supportUi.closeHelp()}
          className="inline-flex h-11 items-center gap-2 rounded-md px-3 text-[13px] font-medium text-ink-2 hover:bg-tile/70 hover:text-ink md:h-8"
        >
          <ListChecks className="size-4" aria-hidden /> С чего начать: мои шаги и порядок дня
        </Link>
        <a
          href="/api/v1/support/help/role.pdf"
          className="inline-flex h-11 items-center gap-2 rounded-md px-3 text-[13px] font-medium text-ink-2 hover:bg-tile/70 hover:text-ink md:h-8"
        >
          <FileDown className="size-4" aria-hidden /> Инструкция по моей роли, PDF
        </a>
        <a
          href="/api/v1/support/help/role.docx"
          className="inline-flex h-11 items-center gap-2 rounded-md px-3 text-[13px] font-medium text-ink-2 hover:bg-tile/70 hover:text-ink md:h-8"
        >
          <FileDown className="size-4" aria-hidden /> Инструкция по моей роли, Word
        </a>
        <Link
          to="/support"
          onClick={() => supportUi.closeHelp()}
          className="inline-flex h-11 items-center gap-2 rounded-md px-3 text-[13px] font-medium text-ink-2 hover:bg-tile/70 hover:text-ink md:h-8"
        >
          Мои обращения и предложения
        </Link>
      </section>

      {h.related.length ? (
        <section>
          <h4 className="mb-1.5 text-[13px] font-semibold">Другие разделы</h4>
          <ul className="flex flex-wrap gap-2">
            {h.related.map((r) => (
              <li key={r.path}>
                <Link
                  to={r.path}
                  onClick={() => supportUi.closeHelp()}
                  className="inline-flex h-11 items-center rounded-md border border-line px-3 text-[13px] hover:border-ink-3 md:h-8"
                >
                  {r.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
